import React, { useState, useEffect, useMemo } from 'react';
import { ExpenseEntry, BUSINESS_CATEGORIES, EXPENSE_COGS_TYPES } from '../types';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

interface DataVisualizationsProps {
  expenses: ExpenseEntry[];
  startDate?: string;
  endDate?: string;
}

interface SummaryData {
  totalExpenses: number;
  byCategoryTotal: Record<string, number>;
  byCategoryAndType: Record<string, Record<string, number>>;
  byExpenseType: Record<string, number>;
  monthlyTotals: Record<string, number>;
}

// Color palette for charts
const CHART_COLORS = [
  'rgba(255, 99, 132, 0.7)',
  'rgba(54, 162, 235, 0.7)',
  'rgba(255, 206, 86, 0.7)',
  'rgba(75, 192, 192, 0.7)',
  'rgba(153, 102, 255, 0.7)',
  'rgba(255, 159, 64, 0.7)',
  'rgba(199, 199, 199, 0.7)',
  'rgba(83, 102, 255, 0.7)',
  'rgba(78, 205, 196, 0.7)',
  'rgba(255, 99, 255, 0.7)',
  'rgba(255, 205, 86, 0.7)',
  'rgba(65, 179, 163, 0.7)',
];

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
};

const formatPercentage = (value: number, total: number): string => {
  if (total === 0) return '0%';
  return `${((value / total) * 100).toFixed(1)}%`;
};

const DataVisualizations: React.FC<DataVisualizationsProps> = ({ 
  expenses, 
  startDate, 
  endDate 
}) => {
  const [activeTab, setActiveTab] = useState<'summary' | 'categories' | 'monthly'>('summary');
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

  // Calculate summary data
  const summaryData: SummaryData = useMemo(() => {
    const result: SummaryData = {
      totalExpenses: 0,
      byCategoryTotal: {},
      byCategoryAndType: {},
      byExpenseType: {},
      monthlyTotals: {},
    };

    // Initialize category totals
    BUSINESS_CATEGORIES.forEach(category => {
      result.byCategoryTotal[category] = 0;
      result.byCategoryAndType[category] = {};
      
      EXPENSE_COGS_TYPES.forEach(type => {
        result.byCategoryAndType[category][type] = 0;
      });
    });

    // Initialize expense type totals
    EXPENSE_COGS_TYPES.forEach(type => {
      result.byExpenseType[type] = 0;
    });

    // Process expenses
    expenses.forEach(expense => {
      // Add to total
      result.totalExpenses += expense.amount;

      // Add to category total
      if (result.byCategoryTotal[expense.category] !== undefined) {
        result.byCategoryTotal[expense.category] += expense.amount;
      } else {
        result.byCategoryTotal[expense.category] = expense.amount;
      }

      // Add to expense type total
      if (result.byExpenseType[expense.expenseCOGS] !== undefined) {
        result.byExpenseType[expense.expenseCOGS] += expense.amount;
      } else {
        result.byExpenseType[expense.expenseCOGS] = expense.amount;
      }

      // Add to category and type breakdown
      if (result.byCategoryAndType[expense.category]) {
        if (result.byCategoryAndType[expense.category][expense.expenseCOGS] !== undefined) {
          result.byCategoryAndType[expense.category][expense.expenseCOGS] += expense.amount;
        } else {
          result.byCategoryAndType[expense.category][expense.expenseCOGS] = expense.amount;
        }
      }

      // Add to monthly totals
      const month = expense.date.substring(0, 7); // Format: YYYY-MM
      if (result.monthlyTotals[month] !== undefined) {
        result.monthlyTotals[month] += expense.amount;
      } else {
        result.monthlyTotals[month] = expense.amount;
      }
    });

    return result;
  }, [expenses]);

  // Prepare chart data
  const categoryChartData = {
    labels: Object.keys(summaryData.byCategoryTotal).filter(cat => summaryData.byCategoryTotal[cat] > 0),
    datasets: [
      {
        data: Object.values(summaryData.byCategoryTotal).filter(amount => amount > 0),
        backgroundColor: CHART_COLORS,
        borderWidth: 1,
      },
    ],
  };

  const expenseTypeChartData = {
    labels: Object.keys(summaryData.byExpenseType).filter(type => summaryData.byExpenseType[type] > 0),
    datasets: [
      {
        data: Object.values(summaryData.byExpenseType).filter(amount => amount > 0),
        backgroundColor: CHART_COLORS.slice(0, Object.keys(summaryData.byExpenseType).length),
        borderWidth: 1,
      },
    ],
  };

  // Prepare monthly chart data (sorted by date)
  const sortedMonths = Object.keys(summaryData.monthlyTotals).sort();
  const monthlyChartData = {
    labels: sortedMonths.map(month => {
      const [year, monthNum] = month.split('-');
      return `${new Date(0, parseInt(monthNum) - 1).toLocaleString('default', { month: 'short' })} ${year}`;
    }),
    datasets: [
      {
        label: 'Monthly Expenses',
        data: sortedMonths.map(month => summaryData.monthlyTotals[month]),
        backgroundColor: 'rgba(75, 192, 192, 0.7)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
    ],
  };

  // Toggle category expansion
  const toggleCategoryExpansion = (category: string) => {
    if (expandedCategories.includes(category)) {
      setExpandedCategories(expandedCategories.filter(cat => cat !== category));
    } else {
      setExpandedCategories([...expandedCategories, category]);
    }
  };

  const dateRangeText = startDate && endDate 
    ? `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`
    : 'All Dates';

  return (
    <div className="space-y-6">
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Expense Summary
            {expenses.length > 0 && (
              <span className="ml-2 text-sm text-gray-500">
                ({dateRangeText})
              </span>
            )}
          </h3>

          {expenses.length === 0 ? (
            <div className="mt-4 text-sm text-gray-500">
              No expense data to display. Upload a statement file to get started.
            </div>
          ) : (
            <div className="mt-5">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                  <button
                    onClick={() => setActiveTab('summary')}
                    className={`${
                      activeTab === 'summary'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Summary
                  </button>
                  <button
                    onClick={() => setActiveTab('categories')}
                    className={`${
                      activeTab === 'categories'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    By Category
                  </button>
                  <button
                    onClick={() => setActiveTab('monthly')}
                    className={`${
                      activeTab === 'monthly'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Monthly
                  </button>
                </nav>
              </div>

              {/* Summary Tab */}
              {activeTab === 'summary' && (
                <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-3">
                      Total Expenses: {formatCurrency(summaryData.totalExpenses)}
                    </h4>
                    <div className="h-64">
                      <Pie 
                        data={categoryChartData} 
                        options={{
                          plugins: {
                            legend: {
                              position: 'right',
                              labels: {
                                boxWidth: 12,
                                font: {
                                  size: 11
                                }
                              }
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  const label = context.label || '';
                                  const value = context.raw as number;
                                  const percentage = formatPercentage(value, summaryData.totalExpenses);
                                  return `${label}: ${formatCurrency(value)} (${percentage})`;
                                }
                              }
                            }
                          },
                          maintainAspectRatio: false,
                        }} 
                      />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-3">
                      By Expense/COGS Type
                    </h4>
                    <div className="h-64">
                      <Pie 
                        data={expenseTypeChartData} 
                        options={{
                          plugins: {
                            legend: {
                              position: 'right',
                              labels: {
                                boxWidth: 12,
                                font: {
                                  size: 11
                                }
                              }
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  const label = context.label || '';
                                  const value = context.raw as number;
                                  const percentage = formatPercentage(value, summaryData.totalExpenses);
                                  return `${label}: ${formatCurrency(value)} (${percentage})`;
                                }
                              }
                            }
                          },
                          maintainAspectRatio: false,
                        }} 
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Categories Tab */}
              {activeTab === 'categories' && (
                <div className="mt-6">
                  <div className="overflow-hidden border-b border-gray-200 sm:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Category
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            % of Total
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {Object.entries(summaryData.byCategoryTotal)
                          .filter(([_, amount]) => amount > 0)
                          .sort(([_, amountA], [__, amountB]) => amountB - amountA)
                          .map(([category, amount]) => (
                            <React.Fragment key={category}>
                              <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleCategoryExpansion(category)}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                                  <span className={`mr-2 ${expandedCategories.includes(category) ? 'transform rotate-90' : ''}`}>
                                    ▶
                                  </span>
                                  {category}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                  {formatCurrency(amount)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                  {formatPercentage(amount, summaryData.totalExpenses)}
                                </td>
                              </tr>
                              {expandedCategories.includes(category) && (
                                <>
                                  {Object.entries(summaryData.byCategoryAndType[category])
                                    .filter(([_, typeAmount]) => typeAmount > 0)
                                    .sort(([_, amountA], [__, amountB]) => amountB - amountA)
                                    .map(([type, typeAmount]) => (
                                      <tr key={`${category}-${type}`} className="bg-gray-50">
                                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500 pl-12">
                                          {type}
                                        </td>
                                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500 text-right">
                                          {formatCurrency(typeAmount)}
                                        </td>
                                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500 text-right">
                                          {formatPercentage(typeAmount, amount)}
                                        </td>
                                      </tr>
                                    ))}
                                </>
                              )}
                            </React.Fragment>
                          ))}
                      </tbody>
                      <tfoot className="bg-gray-100">
                        <tr>
                          <th scope="row" className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            Total
                          </th>
                          <th scope="row" className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                            {formatCurrency(summaryData.totalExpenses)}
                          </th>
                          <th scope="row" className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                            100%
                          </th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Monthly Tab */}
              {activeTab === 'monthly' && (
                <div className="mt-6">
                  <div className="h-96">
                    <Bar 
                      data={monthlyChartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: false,
                          },
                          tooltip: {
                            callbacks: {
                              label: function(context) {
                                const value = context.raw as number;
                                return formatCurrency(value);
                              }
                            }
                          },
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            ticks: {
                              callback: function(value) {
                                return '$' + value.toLocaleString();
                              }
                            }
                          }
                        }
                      }}
                    />
                  </div>
                  
                  <div className="mt-6 overflow-hidden border-b border-gray-200 sm:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Month
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            % of Total
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {sortedMonths.map(month => {
                          const [year, monthNum] = month.split('-');
                          const monthName = new Date(0, parseInt(monthNum) - 1).toLocaleString('default', { month: 'long' });
                          const amount = summaryData.monthlyTotals[month];
                          
                          return (
                            <tr key={month} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {`${monthName} ${year}`}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                {formatCurrency(amount)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                {formatPercentage(amount, summaryData.totalExpenses)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-gray-100">
                        <tr>
                          <th scope="row" className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            Total
                          </th>
                          <th scope="row" className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                            {formatCurrency(summaryData.totalExpenses)}
                          </th>
                          <th scope="row" className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                            100%
                          </th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataVisualizations; 